import React, { useState, useEffect } from "react";
import { apiFetch } from "../utils/api";
import { toast } from "../components/Toast";

const Drivers = () => {
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);

  // Modals
  const [addModal, setAddModal] = useState(false);
  const [importModal, setImportModal] = useState(false);

  // Forms
  const [addForm, setAddForm] = useState({
    name: "",
    phone: "",
    id_type: "",
    id_number: "",
  });
  const [importFile, setImportFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");

  const ITEMS_PER_PAGE = 10;

  // Filter drivers by name or phone
  const filteredDrivers = drivers.filter((driver) => {
    if (!searchQuery.trim()) return true;
    const query = searchQuery.toLowerCase();
    const name = (driver.name || "").toLowerCase();
    const phone = (driver.phone || "").toLowerCase();
    return name.includes(query) || phone.includes(query);
  });

  const totalPages = Math.ceil(filteredDrivers.length / ITEMS_PER_PAGE);
  const paginatedDrivers = filteredDrivers.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE,
  );

  const handlePrevPage = () => setCurrentPage((p) => Math.max(1, p - 1));
  const handleNextPage = () =>
    setCurrentPage((p) => Math.min(totalPages, p + 1));

  const loadDrivers = async () => {
    setLoading(true);
    try {
      const res = await apiFetch("/drivers");
      setDrivers(res.data || []);
    } catch (e) {
      toast.error("Failed to load drivers list");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDrivers();
  }, []);

  const handleAddSubmit = async (e) => {
    e.preventDefault();
    // Validate unique ID number on client side
    const newId = (addForm.id_number || "").toString().trim();
    if (!newId) {
      toast.error("ID Number is required");
      return;
    }
    if (drivers.some((d) => (d.id_number || "").toString().trim() === newId)) {
      toast.error("ID Number must be unique");
      return;
    }

    setSubmitting(true);
    try {
      await apiFetch("/drivers", {
        method: "POST",
        body: JSON.stringify(addForm),
      });
      toast.success("Driver added successfully!");
      setAddModal(false);
      setAddForm({ name: "", phone: "", id_type: "", id_number: "" });
      loadDrivers();
    } catch (e) {
      toast.error(
        e.message === "Driver already exists"
          ? "Already exists"
          : e.message || "Failed to add driver",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (driverId) => {
    const confirmed = window.confirm("Delete this driver?");
    if (!confirmed) return;

    try {
      await apiFetch(`/drivers/${driverId}`, { method: "DELETE" });
      toast.success("Deleted successfully");
      loadDrivers();
    } catch (e) {
      toast.error(
        e.message?.includes("assigned to")
          ? "Cannot delete, currently in use"
          : e.message || "Failed to delete driver",
      );
    }
  };

  const handleImportSubmit = async (e) => {
    e.preventDefault();
    if (!importFile) {
      toast.error("Please select an Excel file");
      return;
    }

    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append("file", importFile);

      const res = await apiFetch("/drivers/import", {
        method: "POST",
        body: formData,
      });

      toast.success(`Imported ${res.data.successCount} drivers successfully!`);
      if (res.data.failedRows.length > 0) {
        toast.error(`${res.data.failedRows.length} rows failed to import`);
        console.log("Failed rows:", res.data.failedRows);
      }
      setImportModal(false);
      setImportFile(null);
      loadDrivers();
    } catch (e) {
      toast.error(e.message || "Import failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "24px",
        }}
      >
        <h1 className="page-title text-gradient" style={{ margin: 0 }}>
          Driver Management
        </h1>
        <div style={{ display: "flex", gap: "12px", alignItems: "center" }}>
          <input
            type="text"
            placeholder="Search by name or phone..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="form-control"
            style={{ width: "250px", height: "40px" }}
          />
          <button
            className="btn-brown"
            onClick={() => setImportModal(true)}
            style={{ height: "40px" }}
          >
            📊 Upload Excel
          </button>
          <button
            className="btn-primary"
            onClick={() => setAddModal(true)}
            style={{ height: "40px" }}
          >
            + Add Driver
          </button>
        </div>
      </div>

      <div
        className="data-table-container glass-panel"
        style={{ display: "flex", flexDirection: "column" }}
      >
        <table className="data-table" style={{ flex: 1, fontSize: "14px" }}>
          <thead>
            <tr style={{ height: "48px" }}>
              <th style={{ padding: "10px 16px" }}>Driver Name</th>
              <th style={{ padding: "10px 16px" }}>ID Type</th>
              <th style={{ padding: "10px 16px" }}>ID Number</th>
              <th style={{ padding: "10px 16px" }}>Phone Number</th>
              <th style={{ padding: "10px 16px" }}>Status</th>
              <th style={{ padding: "10px 16px" }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="6" style={{ textAlign: "center" }}>
                  Loading drivers...
                </td>
              </tr>
            ) : drivers.length === 0 ? (
              <tr>
                <td
                  colSpan="6"
                  style={{ textAlign: "center", color: "var(--text-muted)" }}
                >
                  No drivers found
                </td>
              </tr>
            ) : (
              paginatedDrivers.map((driver) => (
                <tr key={driver.user_id} style={{ height: "48px" }}>
                  <td style={{ fontWeight: "600", padding: "10px 16px" }}>
                    {driver.name}
                  </td>
                  <td style={{ padding: "10px 16px" }}>
                    {driver.id_type || "-"}
                  </td>
                  <td style={{ padding: "10px 16px" }}>
                    {driver.id_number || "-"}
                  </td>
                  <td style={{ color: "#000", padding: "10px 16px" }}>
                    {driver.phone}
                  </td>
                  <td style={{ padding: "10px 16px" }}>
                    <span className="badge badge-active">Active</span>
                  </td>
                  <td style={{ padding: "10px 16px" }}>
                    <button
                      className="btn-danger"
                      onClick={() => handleDelete(driver.user_id)}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        {/* Pagination */}
        {drivers.length > 0 && (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              gap: "16px",
              padding: "16px",
              borderTop: "1px solid var(--border-color)",
            }}
          >
            <button
              className="btn-secondary"
              onClick={handlePrevPage}
              disabled={currentPage === 1}
              style={{ opacity: currentPage === 1 ? 0.5 : 1 }}
            >
              ← Previous
            </button>
            <span style={{ color: "var(--text-muted)" }}>
              Page {currentPage} of {totalPages}
            </span>
            <button
              className="btn-secondary"
              onClick={handleNextPage}
              disabled={currentPage === totalPages}
              style={{ opacity: currentPage === totalPages ? 0.5 : 1 }}
            >
              Next →
            </button>
          </div>
        )}
      </div>

      {/* Add Driver Modal */}
      {addModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h2 className="modal-title">Add New Driver</h2>
              <button
                className="modal-close"
                onClick={() => setAddModal(false)}
              >
                &times;
              </button>
            </div>
            <form onSubmit={handleAddSubmit}>
              <div className="form-group">
                <label className="form-label">Driver Name</label>
                <input
                  type="text"
                  className="form-control"
                  required
                  value={addForm.name}
                  onChange={(e) =>
                    setAddForm({ ...addForm, name: e.target.value })
                  }
                  placeholder="Enter driver's full name"
                />
              </div>
              <div className="form-group">
                <label className="form-label">ID Type</label>
                <select
                  className="form-control"
                  required
                  value={addForm.id_type}
                  onChange={(e) =>
                    setAddForm({ ...addForm, id_type: e.target.value })
                  }
                >
                  <option value="">Select ID type</option>
                  <option value="Aadhar Number">Aadhar Number</option>
                  <option value="Licence">Licence</option>
                  <option value="PAN Card">PAN Card</option>
                  <option value="Passport">Passport</option>
                  <option value="Voters ID">Voters ID</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">ID Number</label>
                <input
                  type="text"
                  className="form-control"
                  required
                  value={addForm.id_number}
                  onChange={(e) =>
                    setAddForm({ ...addForm, id_number: e.target.value })
                  }
                  placeholder="Enter ID number"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Phone Number</label>
                <input
                  type="tel"
                  className="form-control"
                  required
                  value={addForm.phone}
                  onChange={(e) =>
                    setAddForm({ ...addForm, phone: e.target.value })
                  }
                  placeholder="10-digit phone number"
                  pattern="[0-9]{10}"
                />
              </div>
              <button
                type="submit"
                className="btn-primary btn-block"
                disabled={submitting}
              >
                {submitting ? "Adding Driver..." : "Add Driver"}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Import Excel Modal */}
      {importModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h2 className="modal-title">Import Drivers from Excel</h2>
              <button
                className="modal-close"
                onClick={() => setImportModal(false)}
              >
                &times;
              </button>
            </div>
            <form onSubmit={handleImportSubmit}>
              <div className="form-group">
                <label className="form-label">Excel File (.xlsx)</label>
                <input
                  type="file"
                  className="form-control"
                  accept=".xlsx,.xls"
                  required
                  onChange={(e) => setImportFile(e.target.files[0])}
                />
                <small
                  style={{
                    color: "var(--text-muted)",
                    marginTop: "4px",
                    display: "block",
                  }}
                >
                  File should have columns: "Driver Name", "Phone", "ID Type"
                  and "ID Number"
                </small>
              </div>
              <button
                type="submit"
                className="btn-primary btn-block"
                disabled={submitting}
              >
                {submitting ? "Importing..." : "Import Drivers"}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Drivers;
